import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  ButtonGroup,
  Chip,
  IconButton,
  Stack,
  Snackbar,
  Avatar,
  CircularProgress
} from '@mui/material';
import Alert from '@mui/material/Alert';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VideocamIcon from '@mui/icons-material/Videocam';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../common/Forms/quill-editor-fix.css';
import RepositoryService from '../../services/RepositoryService';
import LabelsService from '../../services/LabelsService';
import { getVideoById, updateVideo } from '../../services/VideosService';
import documentsService from '../../services/DocumentsService';

const stripHtml = (html) => {
  if (typeof html !== 'string') return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').trim();
};

/** Asegura filename en Content-Disposition para que ASP.NET enlace IFormFile */
const appendFile = (formData, fieldName, file, fallbackName = 'file.bin') => {
  if (!file) return;
  const filename = file.name || fallbackName;
  formData.append(fieldName, file, filename);
};

const readApiErrorMessage = async (error, fallback) => {
  if (error?.message && !error.message.startsWith('Error al')) {
    return error.message;
  }
  return fallback;
};

const DESCRIPTION_QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};

const DESCRIPTION_QUILL_FORMATS = ['bold', 'italic', 'underline', 'list', 'bullet'];


const ACCENT = '#4daba4';
const ACCENT_DARK = '#3d9993';

const CONTENT_TYPES = [
  { id: 'PDF', label: 'PDF', hint: 'Documento', icon: PictureAsPdfIcon },
  { id: 'Video', label: 'Video', hint: 'MP4 / video', icon: VideocamIcon },
  { id: 'Audio', label: 'Audio', hint: 'Podcast / MP3', icon: AudiotrackIcon },
  { id: 'Link', label: 'Enlace', hint: 'URL externa', icon: LinkIcon },
];

const styleModal = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '96%', sm: '92%', md: 820 },
  maxWidth: 820,
  bgcolor: '#fff',
  borderRadius: 3,
  boxShadow: '0 24px 64px rgba(15, 23, 42, 0.18)',
  p: 0,
  maxHeight: { xs: '94vh', sm: '90vh' },
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  border: '1px solid rgba(77, 171, 164, 0.15)',
};

const sectionCardSx = {
  bgcolor: '#fff',
  border: '1px solid #e8eef0',
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.5 },
};

const sectionTitleSx = {
  fontWeight: 700,
  color: '#1f2937',
  fontSize: '0.95rem',
  mb: 0.5,
};

const sectionHintSx = {
  color: '#6b7280',
  fontSize: '0.78rem',
  mb: 1.75,
  display: 'block',
};

const resolveMediaUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('blob:')) {
    return path;
  }
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${process.env.REACT_APP_LOCATION || ''}${cleanPath}`;
};

const isDocumentType = (tipo) => {
  const normalized = (tipo || '').toLowerCase();
  return normalized === 'documento' || normalized === 'pdf';
};

const isVideoType = (tipo) => (tipo || '').toLowerCase() === 'video';
const isAudioType = (tipo) => {
  const normalized = (tipo || '').toLowerCase();
  return normalized === 'audio' || normalized === 'podcast';
};
const isLinkType = (tipo) => {
  const normalized = (tipo || '').toLowerCase();
  return normalized === 'link' || normalized === 'enlace' || normalized === 'url';
};
const isRepositoryDocumentFamily = (tipo) =>
  isDocumentType(tipo) || isAudioType(tipo) || isLinkType(tipo);

const mapTipoArchivoFromContent = (tipo, contentType) => {
  if (isVideoType(tipo)) return 'Video';
  if (isAudioType(tipo) || isAudioType(contentType)) return 'Audio';
  if (isLinkType(tipo) || isLinkType(contentType)) return 'Link';
  return 'PDF';
};

export default function CrearContenidoModal({ open, onClose, editingItemId, editingItemType, editingItem }) {
  const [categoria, setCategoria] = useState('');
  const [estado, setEstado] = useState('');
  const [tipoArchivo, setTipoArchivo] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [etiquetasActivas, setEtiquetasActivas] = useState([]);
  const [busquedaEtiqueta, setBusquedaEtiqueta] = useState('');
  const [miniatura, setMiniatura] = useState(null);
  const [fileAdjunto, setFileAdjunto] = useState(null);
  const [etiquetasDisponibles, setEtiquetasDisponibles] = useState([]);
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [alerta, setAlerta] = useState({ open: false, tipo: 'success', mensaje: '' });
  const [cargando, setCargando] = useState(false);
  const [miniaturaUrl, setMiniaturaUrl] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [miniaturaPreviewObjectUrl, setMiniaturaPreviewObjectUrl] = useState(null);
  const loadRequestIdRef = useRef(0);
  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);

  const aplicarDatosMaterial = (data, tipo) => {
    const contentType = data.contentType || data.ContentType || '';
    setTipoArchivo(mapTipoArchivoFromContent(tipo, contentType));
    setDatosDesdeServicio(data);
    setFileAdjunto(null);
    setMiniatura(null);
    setExternalUrl(data.externalUrl || data.ExternalUrl || '');
    const rawThumbnail = data.thumbnailPath || data.thumbnailUrl || data.ThumbnailPath || '';
    const thumbnailResolved = resolveMediaUrl(rawThumbnail);
    setMiniaturaUrl(
      thumbnailResolved && data.updatedAt
        ? `${thumbnailResolved}${thumbnailResolved.includes('?') ? '&' : '?'}v=${encodeURIComponent(data.updatedAt)}`
        : thumbnailResolved
    );
    setFileUrl(
      resolveMediaUrl(
        data.path || data.fileUrl || data.filePath || data.videoUrl || data.videoPath || data.Path
      ) || data.externalUrl || data.ExternalUrl || null
    );
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (thumbnailInputRef.current) thumbnailInputRef.current.value = '';
  };

  useEffect(() => {
    if (!open) return;

    const fetchEtiquetas = async () => {
      try {
        const response = await LabelsService.getAllLabels();
        const nombresEtiquetas = response.map(label => label.name);
        setEtiquetasDisponibles(nombresEtiquetas);
      } catch (error) {
        console.error('Error al obtener las etiquetas:', error);
        setAlerta({
          open: true,
          tipo: 'error',
          mensaje: 'Error al cargar las etiquetas disponibles',
        });
      }
    };

    fetchEtiquetas();

    if (editingItemId) {
      cargarDatosEdicion();
    } else {
      resetearEstado();
    }

    return () => {
      loadRequestIdRef.current += 1;
    };
  }, [open, editingItemId, editingItemType]);

  useEffect(() => {
    if (!miniatura) {
      setMiniaturaPreviewObjectUrl(null);
      return undefined;
    }

    const objectUrl = URL.createObjectURL(miniatura);
    setMiniaturaPreviewObjectUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [miniatura]);

  const cargarDatosEdicion = async () => {
    const requestId = ++loadRequestIdRef.current;
    const tipoNormalizado = (editingItemType || editingItem?.tipo || '').toLowerCase();
    const esDocumento = isRepositoryDocumentFamily(tipoNormalizado);
    const esVideo = isVideoType(tipoNormalizado);

    if (!esDocumento && !esVideo) {
      setAlerta({
        open: true,
        tipo: 'error',
        mensaje: 'No se pudo determinar el tipo de material a editar.',
      });
      onClose();
      return;
    }

    setCargando(true);

    // Prefill inmediato desde la fila para evitar cruces por respuestas tardías
    if (editingItem?.raw) {
      aplicarDatosMaterial(editingItem.raw, tipoNormalizado);
    }

    try {
      if (esDocumento) {
        const docData = await documentsService.getDocumentById(editingItemId);
        if (requestId !== loadRequestIdRef.current) return;
        if (docData?.id != null && Number(docData.id) !== Number(editingItemId)) {
          throw new Error('El documento cargado no coincide con el material seleccionado.');
        }
        aplicarDatosMaterial(docData, 'documento');
        setCargando(false);
        return;
      }

      const videoData = await getVideoById(editingItemId);
      if (requestId !== loadRequestIdRef.current) return;
      if (videoData?.id != null && Number(videoData.id) !== Number(editingItemId)) {
        throw new Error('El video cargado no coincide con el material seleccionado.');
      }
      aplicarDatosMaterial(videoData, 'video');
      setCargando(false);
    } catch (error) {
      if (requestId !== loadRequestIdRef.current) return;
      console.error('Error al cargar datos para edición:', error);
      setAlerta({
        open: true,
        tipo: 'error',
        mensaje: error.message || 'Error al cargar los datos del contenido',
      });
      setCargando(false);
      onClose();
    }
  };

  const setDatosDesdeServicio = (data) => {
    setNombre(data.title || '');
    setDescripcion(data.description || '');
    setCategoria(data.category || '');
    setEstado(data.isValid ? 'Vigente' : 'No vigente');
    
    // Manejar diferentes formatos de etiquetas
    if (data.labels) {
      if (Array.isArray(data.labels)) {
        setEtiquetasActivas(data.labels);
      } else if (typeof data.labels === 'string') {
        setEtiquetasActivas(data.labels.split(',').map(l => l.trim()).filter(l => l));
      } else {
        setEtiquetasActivas([]);
      }
    } else {
      setEtiquetasActivas([]);
    }
  };

  const resetearEstado = () => {
    setNombre('');
    setDescripcion('');
    setEtiquetasActivas([]);
    setCategoria('');
    setEstado('');
    setTipoArchivo('');
    setExternalUrl('');
    setFileAdjunto(null);
    setMiniatura(null);
    setBusquedaEtiqueta('');
    setMiniaturaUrl(null);
    setFileUrl(null);
  };

  const agregarEtiqueta = async (etiqueta) => {
    if (!etiquetasActivas.includes(etiqueta)) {
      setEtiquetasActivas([...etiquetasActivas, etiqueta]);
      
      if (!etiquetasDisponibles.includes(etiqueta)) {
        try {
          const etiquetaData = { name: etiqueta, isProfile: false };
          await LabelsService.createLabel(etiquetaData);
          setEtiquetasDisponibles([...etiquetasDisponibles, etiqueta]);
        } catch (error) {
          console.error('Error al crear la etiqueta:', error);
          setAlerta({
            open: true,
            tipo: 'error',
            mensaje: 'Error al crear la nueva etiqueta',
          });
        }
      }
    }
  };

  const quitarEtiqueta = (etiqueta) => {
    setEtiquetasActivas(etiquetasActivas.filter((e) => e !== etiqueta));
  };

  const etiquetasFiltradas = etiquetasDisponibles.filter((etiqueta) =>
    etiqueta.toLowerCase().includes(busquedaEtiqueta.toLowerCase())
  );

  const puedeGuardar = () => {
    // Validar campos obligatorios (descripción puede venir como HTML del editor)
    if (!nombre.trim() || !stripHtml(descripcion) || !categoria || !estado) {
      return false;
    }
    
    if (!tipoArchivo) return false;
    
    // Para edición, no es obligatorio cambiar el archivo/miniatura
    if (editingItemId) {
      if (tipoArchivo === 'Link' && !externalUrl.trim() && !fileUrl) return false;
      return true;
    }

    if (tipoArchivo === 'Link') {
      return Boolean(externalUrl.trim());
    }
    
    // Para creación, se requiere archivo
    if (!fileAdjunto) return false;
    
    // Para videos nuevos, la miniatura es obligatoria
    if (tipoArchivo === 'Video' && !miniatura) return false;
    
    return true;
  };

  const handleGuardar = async () => {
    const user_id = localStorage.getItem('usuarioConCedula'); 
    const user_idJSON = JSON.parse(user_id);

    try {
      setCargando(true);
      
      if (editingItemId) {
          const tipoEdicion = editingItemType || editingItem?.tipo || tipoArchivo || '';
          const esDocumentoFamily = isRepositoryDocumentFamily(tipoEdicion) || ['PDF', 'Audio', 'Link'].includes(tipoArchivo);
          const esVideo = isVideoType(tipoEdicion) || tipoArchivo === 'Video';

          if (!esDocumentoFamily && !esVideo) {
            throw new Error('No se pudo determinar el tipo de material a actualizar.');
          }

          const formData = new FormData();
          formData.append('Title', nombre);
          formData.append('Description', descripcion);
          formData.append('Category', categoria);
          formData.append('IsValid', String(estado === 'Vigente'));
          formData.append('Visibility', 'public');
          formData.append('Labels', etiquetasActivas.join(', '));

          if (esDocumentoFamily) {
            const contentType =
              tipoArchivo === 'Audio' ? 'audio' : tipoArchivo === 'Link' ? 'link' : 'document';
            formData.append('ContentType', contentType);
            if (tipoArchivo === 'Link') {
              formData.append('ExternalUrl', externalUrl.trim());
            } else {
              appendFile(formData, 'File', fileAdjunto, tipoArchivo === 'Audio' ? 'audio.mp3' : 'documento.pdf');
            }
            appendFile(formData, 'Thumbnail', miniatura, 'thumbnail.jpg');
            await documentsService.updateDocument(editingItemId, formData);
          } else {
            appendFile(formData, 'Video', fileAdjunto, 'video.mp4');
            appendFile(formData, 'Thumbnail', miniatura, 'thumbnail.jpg');
            const updated = await updateVideo(editingItemId, formData);
            if (miniatura && !updated?.thumbnailPath && !updated?.ThumbnailPath) {
              throw new Error('El video se actualizó pero la miniatura no quedó guardada.');
            }
          }
        
        setAlerta({
          open: true,
          tipo: 'success',
          mensaje: 'Contenido actualizado correctamente.',
        });
      } else {
        if (tipoArchivo === 'Video' && !miniatura) {
          throw new Error('Debe seleccionar una miniatura para el video.');
        }
        if (tipoArchivo === 'Link' && !externalUrl.trim()) {
          throw new Error('Debe indicar la URL del enlace.');
        }

        const formData = new FormData();
        formData.append('Title', nombre);
        formData.append('Description', descripcion);
        formData.append('UploadedBy', String(user_idJSON.id));
        formData.append('Category', categoria);
        formData.append('IsValid', String(estado === 'Vigente'));
        formData.append('Visibility', 'public');
        formData.append('Labels', etiquetasActivas.join(', '));

        if (tipoArchivo === 'Video') {
          appendFile(formData, 'Video', fileAdjunto, 'video.mp4');
          appendFile(formData, 'Thumbnail', miniatura, 'thumbnail.jpg');
        } else if (tipoArchivo === 'PDF') {
          formData.append('ContentType', 'document');
          appendFile(formData, 'File', fileAdjunto, 'documento.pdf');
          appendFile(formData, 'Thumbnail', miniatura, 'thumbnail.jpg');
        } else if (tipoArchivo === 'Audio') {
          formData.append('ContentType', 'audio');
          appendFile(formData, 'File', fileAdjunto, 'audio.mp3');
          appendFile(formData, 'Thumbnail', miniatura, 'thumbnail.jpg');
        } else if (tipoArchivo === 'Link') {
          formData.append('ContentType', 'link');
          formData.append('ExternalUrl', externalUrl.trim());
          appendFile(formData, 'Thumbnail', miniatura, 'thumbnail.jpg');
        }
        
        const created = await RepositoryService.uploadArchivo(formData, tipoArchivo === 'Video' ? 'Video' : 'PDF');
        if (tipoArchivo === 'Video' && !created?.thumbnailPath && !created?.ThumbnailPath) {
          throw new Error('El video se creó pero la miniatura no quedó guardada.');
        }
        
        setAlerta({
          open: true,
          tipo: 'success',
          mensaje: 'Contenido creado correctamente.',
        });
      }

      resetearEstado();
      onClose(true);
    } catch (error) {
      console.error('Error al guardar el contenido:', error);
      const mensaje = await readApiErrorMessage(
        error,
        `Error al ${editingItemId ? 'actualizar' : 'crear'} el contenido. Intenta nuevamente.`
      );
      setAlerta({
        open: true,
        tipo: 'error',
        mensaje,
      });
    } finally {
      setCargando(false);
    }
  };

  const renderMiniaturaPreview = () => {
    if (miniatura && miniaturaPreviewObjectUrl) {
      return (
        <Box mt={2}>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Nueva miniatura seleccionada:
          </Typography>
          <Box
            component="img"
            src={miniaturaPreviewObjectUrl}
            alt="Miniatura"
            sx={{ 
              width: '100%', 
              maxWidth: 200, 
              borderRadius: 2, 
              boxShadow: 1,
              border: '1px solid',
              borderColor: 'divider',
              aspectRatio: '16/9',
              objectFit: 'cover'
            }}
          />
        </Box>
      );
    } else if (miniaturaUrl) {
      return (
        <Box mt={2}>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Miniatura actual:
          </Typography>
          <Box
            component="img"
            src={miniaturaUrl}
            alt="Miniatura actual"
            sx={{ 
              width: '100%', 
              maxWidth: 200, 
              borderRadius: 2, 
              boxShadow: 1,
              border: '1px solid',
              borderColor: 'divider',
              aspectRatio: '16/9',
              objectFit: 'cover'
            }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </Box>
      );
    }
    return null;
  };

  const renderFilePreview = () => {
    if (fileAdjunto) {
      return (
        <Box 
          mt={1} 
          display="flex" 
          alignItems="center" 
          gap={1}
          p={1.5}
          border="1px solid"
          borderColor="divider"
          borderRadius={2}
        >
          <Avatar sx={{
            bgcolor: ACCENT,
            width: 32,
            height: 32,
            fontSize: '0.7rem',
            fontWeight: 700,
          }}>
            {tipoArchivo === 'PDF' ? 'PDF' : tipoArchivo === 'Audio' ? 'AUD' : 'VID'}
          </Avatar>
          <Box flexGrow={1}>
            <Typography variant="body2" noWrap>
              {fileAdjunto.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {(fileAdjunto.size / 1024 / 1024).toFixed(2)} MB
            </Typography>
          </Box>
          <IconButton
            color="error"
            size="small"
            onClick={() => setFileAdjunto(null)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    } else if (fileUrl) {
      return (
        <Box 
          mt={1} 
          display="flex" 
          alignItems="center" 
          gap={1}
          p={1.5}
          border="1px solid"
          borderColor="divider"
          borderRadius={2}
        >
          <Avatar sx={{
            bgcolor: ACCENT,
            width: 32,
            height: 32,
            fontSize: '0.7rem',
            fontWeight: 700,
          }}>
            {tipoArchivo === 'PDF' ? 'PDF' : tipoArchivo === 'Audio' ? 'AUD' : 'VID'}
          </Avatar>
          <Box flexGrow={1} minWidth={0}>
            <Typography variant="body2" noWrap>
              Archivo actual ({tipoArchivo})
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {decodeURIComponent((fileUrl || '').split('/').pop() || `${tipoArchivo === 'Video' ? 'Video' : 'Documento'} existente`)}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            (Se mantendrá)
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (cargando && editingItemId) {
    return (
      <Modal open={open} onClose={onClose}>
        <Box sx={styleModal} display="flex" justifyContent="center" alignItems="center" minHeight={{ xs: 150, sm: 200 }}>
          <CircularProgress size={{ xs: 40, sm: 50, md: 60 }} sx={{ color: '#4daba4' }} />
        </Box>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={() => { resetearEstado(); onClose(); }}>
      <Box sx={styleModal}>
        <Box
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: 2,
            borderBottom: '1px solid #e8eef0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            bgcolor: '#fff',
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#111827' }}>
              {editingItemId ? 'Editar contenido' : 'Agregar contenido'}
            </Typography>
            <Typography sx={{ color: '#6b7280', fontSize: '0.82rem', mt: 0.25 }}>
              {editingItemId
                ? 'Actualiza los datos del material del repositorio'
                : 'Elige el tipo, sube el archivo y completa la información'}
            </Typography>
          </Box>
          <IconButton
            onClick={() => { resetearEstado(); onClose(); }}
            disabled={cargando}
            size="small"
            sx={{ color: '#6b7280' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            bgcolor: '#f7fafb',
            px: { xs: 2, sm: 3 },
            py: 2.5,
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-thumb': { background: ACCENT, borderRadius: 8 },
          }}
        >
          <Stack spacing={2.25}>
            {/* 1. Tipo */}
            <Box sx={sectionCardSx}>
              <Typography sx={sectionTitleSx}>1. Tipo de contenido</Typography>
              <Typography sx={sectionHintSx}>
                {editingItemId
                  ? 'El tipo no se puede cambiar al editar'
                  : 'Selecciona qué vas a cargar'}
              </Typography>
              <Grid container spacing={1.25}>
                {CONTENT_TYPES.map(({ id, label, hint, icon: Icon }) => {
                  const selected = tipoArchivo === id;
                  return (
                    <Grid item xs={6} sm={3} key={id}>
                      <Box
                        component="button"
                        type="button"
                        disabled={!!editingItemId}
                        onClick={() => {
                          setTipoArchivo(id);
                          setFileAdjunto(null);
                          setExternalUrl('');
                          if (id === 'PDF') setMiniatura(null);
                        }}
                        sx={{
                          width: '100%',
                          border: selected ? `2px solid ${ACCENT}` : '1px solid #e5e7eb',
                          bgcolor: selected ? 'rgba(77, 171, 164, 0.08)' : '#fff',
                          borderRadius: 2,
                          p: 1.5,
                          cursor: editingItemId ? 'not-allowed' : 'pointer',
                          opacity: editingItemId && !selected ? 0.45 : 1,
                          textAlign: 'left',
                          transition: 'all 0.15s ease',
                          '&:hover': editingItemId
                            ? {}
                            : {
                                borderColor: ACCENT,
                                bgcolor: 'rgba(77, 171, 164, 0.06)',
                              },
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            bgcolor: selected ? ACCENT : '#f1f5f9',
                            color: selected ? '#fff' : ACCENT,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 1,
                          }}
                        >
                          <Icon fontSize="small" />
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#111827' }}>
                          {label}
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#6b7280' }}>{hint}</Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>

            {/* 2. Archivo / media */}
            {tipoArchivo && (
              <Box sx={sectionCardSx}>
                <Typography sx={sectionTitleSx}>2. Archivo y portada</Typography>
                <Typography sx={sectionHintSx}>
                  {tipoArchivo === 'Link'
                    ? 'Pega la URL y, si quieres, una imagen de portada'
                    : tipoArchivo === 'Video'
                      ? 'Sube el video y su miniatura (obligatoria al crear)'
                      : 'Sube el archivo principal y una portada opcional'}
                </Typography>

                <Stack spacing={2}>
                  {tipoArchivo === 'Link' ? (
                    <TextField
                      fullWidth
                      size="small"
                      label="URL del enlace"
                      placeholder="https://..."
                      value={externalUrl}
                      onChange={(e) => setExternalUrl(e.target.value)}
                    />
                  ) : (
                    <Box>
                      <label htmlFor="adjunto-input">
                        <input
                          id="adjunto-input"
                          ref={fileInputRef}
                          type="file"
                          accept={
                            tipoArchivo === 'Video'
                              ? 'video/mp4,video/*'
                              : tipoArchivo === 'Audio'
                                ? 'audio/*,.mp3,.wav,.m4a,.ogg'
                                : 'application/pdf'
                          }
                          style={{ display: 'none' }}
                          onChange={(e) => setFileAdjunto(e.target.files?.[0] || null)}
                        />
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<UploadFileIcon />}
                          fullWidth
                          sx={{
                            textTransform: 'none',
                            borderStyle: 'dashed',
                            borderWidth: 1.5,
                            borderColor: ACCENT,
                            color: ACCENT,
                            py: 1.5,
                            '&:hover': {
                              borderStyle: 'dashed',
                              borderWidth: 1.5,
                              borderColor: ACCENT_DARK,
                              bgcolor: 'rgba(77, 171, 164, 0.06)',
                            },
                          }}
                        >
                          {fileAdjunto ? 'Cambiar archivo' : `Seleccionar ${tipoArchivo === 'PDF' ? 'PDF' : tipoArchivo.toLowerCase()}`}
                        </Button>
                      </label>
                      {renderFilePreview()}
                      {editingItemId && !fileAdjunto && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          Si no eliges un archivo nuevo, se mantiene el actual
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Box>
                    <label htmlFor="miniatura-input">
                      <input
                        id="miniatura-input"
                        ref={thumbnailInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                        style={{ display: 'none' }}
                        onChange={(e) => setMiniatura(e.target.files?.[0] || null)}
                      />
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<ImageIcon />}
                        fullWidth
                        sx={{
                          textTransform: 'none',
                          borderColor: '#d1d5db',
                          color: '#374151',
                          py: 1.25,
                          '&:hover': {
                            borderColor: ACCENT,
                            color: ACCENT,
                            bgcolor: 'rgba(77, 171, 164, 0.04)',
                          },
                        }}
                      >
                        {miniatura || miniaturaUrl ? 'Cambiar portada' : 'Agregar portada / miniatura'}
                        {tipoArchivo === 'Video' && !editingItemId ? ' (obligatoria)' : ' (opcional)'}
                      </Button>
                    </label>
                    {renderMiniaturaPreview()}
                    {!editingItemId && tipoArchivo === 'Video' && !miniatura && (
                      <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
                        La miniatura es obligatoria al crear un video
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Box>
            )}

            {/* 3. Información */}
            <Box sx={sectionCardSx}>
              <Typography sx={sectionTitleSx}>3. Información</Typography>
              <Typography sx={sectionHintSx}>Nombre y descripción visibles en el repositorio</Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nombre"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Ej: Guía de manejo de diabetes"
                  helperText="Entre 10 y 80 caracteres"
                />
                <Box>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', mb: 1 }}>
                    Descripción
                  </Typography>
                  <Box
                    sx={{
                      border: '1px solid #e5e7eb',
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: '#fafbfc',
                      height: 160,
                      display: 'flex',
                      flexDirection: 'column',
                      '&:focus-within': {
                        borderColor: ACCENT,
                        boxShadow: '0 0 0 3px rgba(77, 171, 164, 0.12)',
                      },
                      '& .ql-toolbar': {
                        border: 'none',
                        borderBottom: '1px solid #e5e7eb',
                        background: '#f8fafc',
                      },
                      '& .ql-container': {
                        border: 'none',
                        flex: 1,
                        minHeight: 0,
                        fontSize: '0.9rem',
                      },
                    }}
                  >
                    <ReactQuill
                      theme="snow"
                      value={descripcion || ''}
                      onChange={setDescripcion}
                      modules={DESCRIPTION_QUILL_MODULES}
                      formats={DESCRIPTION_QUILL_FORMATS}
                      placeholder="Describe el contenido..."
                      style={{ flex: 1, minHeight: 0, height: '100%' }}
                    />
                  </Box>
                </Box>
              </Stack>
            </Box>

            {/* 4. Clasificación */}
            <Box sx={sectionCardSx}>
              <Typography sx={sectionTitleSx}>4. Clasificación</Typography>
              <Typography sx={sectionHintSx}>
                Categoría, vigencia y etiquetas para filtrar el contenido
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Categoría</InputLabel>
                    <Select
                      value={categoria}
                      label="Categoría"
                      onChange={(e) => setCategoria(e.target.value)}
                    >
                      <MenuItem value="Monografía">Monografía</MenuItem>
                      <MenuItem value="Ayuda Visual">Ayuda Visual</MenuItem>
                      <MenuItem value="Guía de Manejo">Guía de Manejo</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ButtonGroup fullWidth size="small" sx={{ height: 40 }}>
                    <Button
                      variant={estado === 'Vigente' ? 'contained' : 'outlined'}
                      onClick={() => setEstado('Vigente')}
                      sx={{
                        textTransform: 'none',
                        bgcolor: estado === 'Vigente' ? ACCENT : 'transparent',
                        borderColor: ACCENT,
                        color: estado === 'Vigente' ? '#fff' : ACCENT,
                        '&:hover': {
                          bgcolor: estado === 'Vigente' ? ACCENT_DARK : 'rgba(77, 171, 164, 0.08)',
                          borderColor: ACCENT,
                        },
                      }}
                    >
                      Vigente
                    </Button>
                    <Button
                      variant={estado === 'No vigente' ? 'contained' : 'outlined'}
                      onClick={() => setEstado('No vigente')}
                      sx={{
                        textTransform: 'none',
                        bgcolor: estado === 'No vigente' ? '#ef4444' : 'transparent',
                        borderColor: '#ef4444',
                        color: estado === 'No vigente' ? '#fff' : '#ef4444',
                        '&:hover': {
                          bgcolor: estado === 'No vigente' ? '#dc2626' : 'rgba(239, 68, 68, 0.08)',
                          borderColor: '#ef4444',
                        },
                      }}
                    >
                      No vigente
                    </Button>
                  </ButtonGroup>
                </Grid>
              </Grid>

              <Box mt={2.5}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', mb: 1 }}>
                  Etiquetas {etiquetasActivas.length > 0 ? `(${etiquetasActivas.length})` : ''}
                </Typography>
                <Box display="flex" gap={1} mb={1.5}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Buscar o crear etiqueta"
                    value={busquedaEtiqueta}
                    onChange={(e) => setBusquedaEtiqueta(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && busquedaEtiqueta.trim() && !etiquetasDisponibles.includes(busquedaEtiqueta)) {
                        agregarEtiqueta(busquedaEtiqueta);
                        setBusquedaEtiqueta('');
                      }
                    }}
                  />
                  {busquedaEtiqueta.trim() && !etiquetasDisponibles.includes(busquedaEtiqueta) && (
                    <IconButton
                      onClick={() => {
                        agregarEtiqueta(busquedaEtiqueta);
                        setBusquedaEtiqueta('');
                      }}
                      sx={{ bgcolor: ACCENT, color: '#fff', borderRadius: 1.5, '&:hover': { bgcolor: ACCENT_DARK } }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                {etiquetasActivas.length > 0 && (
                  <Box display="flex" flexWrap="wrap" gap={0.75} mb={1.5}>
                    {etiquetasActivas.map((etiqueta) => (
                      <Chip
                        key={etiqueta}
                        label={etiqueta}
                        size="small"
                        onDelete={() => quitarEtiqueta(etiqueta)}
                        sx={{
                          bgcolor: ACCENT,
                          color: '#fff',
                          fontWeight: 500,
                          '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.85)' },
                        }}
                      />
                    ))}
                  </Box>
                )}

                <Box
                  sx={{
                    maxHeight: 140,
                    overflowY: 'auto',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.75,
                    p: 1.25,
                    bgcolor: '#f8fafc',
                    borderRadius: 2,
                    border: '1px solid #e5e7eb',
                  }}
                >
                  {etiquetasFiltradas.length > 0 ? (
                    etiquetasFiltradas.slice(0, 24).map((etiqueta) => {
                      const active = etiquetasActivas.includes(etiqueta);
                      return (
                        <Chip
                          key={etiqueta}
                          label={etiqueta}
                          size="small"
                          clickable
                          onClick={() => (active ? quitarEtiqueta(etiqueta) : agregarEtiqueta(etiqueta))}
                          icon={active ? <CheckIcon sx={{ fontSize: '14px !important' }} /> : undefined}
                          variant={active ? 'filled' : 'outlined'}
                          sx={{
                            bgcolor: active ? 'rgba(77, 171, 164, 0.15)' : '#fff',
                            color: ACCENT,
                            borderColor: ACCENT,
                            fontWeight: 500,
                            '& .MuiChip-icon': { color: ACCENT },
                          }}
                        />
                      );
                    })
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No hay etiquetas para mostrar
                    </Typography>
                  )}
                  {etiquetasFiltradas.length > 24 && (
                    <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mt: 0.5 }}>
                      Escribe para filtrar las {etiquetasFiltradas.length - 24} restantes
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Stack>
        </Box>

        <Snackbar
          open={alerta.open}
          autoHideDuration={6000}
          onClose={() => setAlerta({ ...alerta, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={alerta.tipo}
            onClose={() => setAlerta({ ...alerta, open: false })}
            sx={{ width: '100%', boxShadow: 3, borderRadius: 2 }}
          >
            {alerta.mensaje}
          </Alert>
        </Snackbar>

        <Box
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: 2,
            borderTop: '1px solid #e8eef0',
            bgcolor: '#fff',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => { resetearEstado(); onClose(); }}
            disabled={cargando}
            sx={{
              textTransform: 'none',
              borderColor: '#d1d5db',
              color: '#374151',
              px: 2.5,
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!puedeGuardar() || cargando}
            onClick={handleGuardar}
            sx={{
              textTransform: 'none',
              bgcolor: ACCENT,
              px: 2.75,
              fontWeight: 600,
              '&:hover': { bgcolor: ACCENT_DARK },
              '&.Mui-disabled': { bgcolor: 'rgba(77, 171, 164, 0.45)', color: '#fff' },
            }}
          >
            {cargando ? (
              <CircularProgress size={22} sx={{ color: 'white' }} />
            ) : editingItemId ? (
              'Guardar cambios'
            ) : (
              'Guardar contenido'
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}